import { AxiosError } from 'axios';
import { toastError } from '@/shared/lib/utils/toast';
import { CancelledError } from '@tanstack/react-query';

/**
 * Типы данных ошибки API
 */
type ErrorData = {
  detail?: string | Array<{ msg?: string; message?: string }> | { message: string };
  message?: string;
};

/**
 * Извлекает сообщение об ошибке из различных форматов ответа API
 * @param errorData - Данные ошибки из ответа API
 * @returns Сообщение об ошибке
 */
export const extractErrorMessage = (errorData: unknown): string => {
  const defaultMessage = 'Произошла ошибка при выполнении запроса';

  if (!errorData || typeof errorData !== 'object') {
    return defaultMessage;
  }

  const data = errorData as ErrorData;

  // Формат 1: detail - строка
  if (typeof data.detail === 'string') {
    return data.detail;
  }

  // Формат 2: detail - массив объектов с msg или message
  if (Array.isArray(data.detail) && data.detail.length > 0) {
    const firstError = data.detail[0];
    if (firstError.msg) {
      return firstError.msg;
    }
    if (firstError.message) {
      return firstError.message;
    }
  }

  // Формат 3: detail - объект (не массив) с message
  if (data.detail && typeof data.detail === 'object' && !Array.isArray(data.detail) && 'message' in data.detail) {
    return data.detail.message;
  }

  // Формат 4: прямое поле message
  if (data.message) {
    return data.message;
  }

  return defaultMessage;
};

let unauthorizedHandler: (() => void) | null = null;

/**
 * Регистрирует обработчик ошибки 401 (Unauthorized)
 * Вызывается из слоя app для установки callback с доступом к auth store.
 * Возвращает функцию снятия обработчика
 */
export const registerUnauthorizedHandler = (handler: () => void): (() => void) => {
  unauthorizedHandler = handler;

  return () => {
    // Снимается только свой обработчик: к моменту снятия зарегистрированным
    // может оказаться уже следующий
    if (unauthorizedHandler === handler) {
      unauthorizedHandler = null;
    }
  };
};

/**
 * Обрабатывает ошибку 401 (Unauthorized)
 * Вызывает зарегистрированный обработчик для перенаправления на страницу авторизации
 */
export const handleUnauthorizedError = (): void => {
  unauthorizedHandler?.();
};

/**
 * Обрабатывает ошибки Axios
 * @param error - Ошибка Axios
 * @returns Отклоненный Promise с данными ошибки
 */
export const handleAxiosError = (error: AxiosError): Promise<{ data: ErrorData; status?: number }> => {
  const status = error.response?.status;
  const errorData = error.response?.data;

  // Handle unauthorized errors (401) - не показываем toast, так как происходит редирект
  if (status === 401) {
    handleUnauthorizedError();
    return Promise.reject({ data: errorData, status });
  }

  // Извлекаем и показываем сообщение об ошибке
  const errorMessage = extractErrorMessage(errorData);
  if (!error.config?.silentError && !(error instanceof CancelledError) && error.code !== 'ERR_CANCELED') {
    toastError('Error', {
      description: errorMessage,
    });
  }

  return Promise.reject({ data: errorData, status });
};

/**
 * Обрабатывает не-Axios ошибки (сетевые ошибки и другие типы)
 * @param error - Ошибка любого типа
 * @returns Отклоненный Promise
 */
export const handleUnknownError = (error: unknown): Promise<never> => {
  const errorMessage = error instanceof Error ? error.message : 'Произошла неизвестная ошибка';
  toastError(errorMessage);
  return Promise.reject(error);
};
